import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcCafe } from './CalccafeContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './ProducaoCafe.css';

// Funções de formatação
const formatValueForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(String(value).replace(/\D/g, '')) / 100;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const ProducaoCafe = () => {
  const { dados, setDados } = useCalcCafe();
  const navigate = useNavigate();
  const [mostrarResultados, setMostrarResultados] = useState(false);

  useEffect(() => {
    if (!dados.producao) {
      setDados(prev => ({
        ...prev,
        producao: {
          producaoTotal: '', areaPlantada: '', colaboradores: '',
          dataSelecionada: new Date().toISOString().split('T')[0],
        }
      }));
    }
    if (dados.resultadosProducao) {
      setMostrarResultados(true);
    }
  }, [dados.producao, dados.resultadosProducao, setDados]);

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({
      ...prev,
      producao: { ...prev.producao, [name]: rawValue }
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDados(prev => ({ ...prev, producao: { ...prev.producao, [name]: value } }));
  };

  const handleCalcular = () => {
    const p = dados.producao;
    if (!p || !p.producaoTotal || !p.areaPlantada || !p.colaboradores) {
      alert("Por favor, preencha todos os campos do formulário.");
      return;
    }

    const producaoTotal = (Number(p.producaoTotal) || 0) / 100;
    const areaPlantada = (Number(p.areaPlantada) || 0) / 100;
    const colaboradores = (Number(p.colaboradores) || 0) / 100;

    const resultados = {
      sacasPorHectare: areaPlantada ? (producaoTotal / areaPlantada).toFixed(2) : "0.00",
      sacasPorColaborador: colaboradores ? (producaoTotal / colaboradores).toFixed(2) : "0.00",
    };

    setDados(prev => ({ ...prev, resultadosProducao: resultados }));
    setMostrarResultados(true);
  };
  
  const handleDownloadPDF = () => {
    const p = dados.producao;
    const r = dados.resultadosProducao;
    if (!r) {
        alert("Calcule os resultados primeiro para gerar o PDF.");
        return;
    }
    const doc = new jsPDF();
    const primaryColor = [111, 78, 55]; // Marrom #6f4e37
    const darkColor = [62, 39, 35];     // Marrom escuro #3E2723
    
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text("Relatório de Produção - CalcCafé", 105, 12, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const dataFormatada = new Date(p.dataSelecionada).toLocaleDateString('pt-BR', {timeZone: 'UTC'});
    doc.text(`Análise para ${dataFormatada}`, 105, 20, { align: 'center' });

    const dadosInseridosBody = [
        ['Produção Total (sacas)', `${formatValueForDisplay(p.producaoTotal)} sc`],
        ['Área Plantada (hectares)', `${formatValueForDisplay(p.areaPlantada)} ha`],
        ['Nº de Colaboradores', `${formatValueForDisplay(p.colaboradores)}`],
    ];

    autoTable(doc, {
        startY: 40,
        head: [['Descrição', 'Valor Inserido']],
        body: dadosInseridosBody,
        theme: 'striped',
        headStyles: { fillColor: darkColor, fontStyle: 'bold' },
        styles: { fontSize: 10, textColor: [50, 50, 50] },
        margin: { left: 14, right: 14 }
    });

    const resultadosBody = [
        ['Sacas por Hectare', `${r.sacasPorHectare} sc/ha`],
        ['Sacas por Colaborador', `${r.sacasPorColaborador} sc/colab`],
    ];

    autoTable(doc, {
        head: [['Indicador de Produtividade', 'Resultado Calculado']],
        body: resultadosBody,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, fontStyle: 'bold' },
        styles: { fontSize: 10, textColor: [50, 50, 50] },
        margin: { left: 14, right: 14 }
    });

    const finalY = (doc).lastAutoTable.finalY;
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(`Gerado por CustosAgro - Copyright © ${new Date().getFullYear()}. Todos os Direitos Reservados.`, 105, finalY + 15, { align: 'center' });
    
    doc.save("relatorio_producao_calccafe.pdf");
  };

  const handleLimparDados = () => {
    if(window.confirm("Tem certeza que deseja limpar todos os dados desta tela?")) {
        setDados(prev => {
            const { producao, resultadosProducao, ...resto } = prev;
            localStorage.setItem('calcCafeData', JSON.stringify(resto));
            return resto;
        });
        setMostrarResultados(false);
    }
  };

  return (
    <div className="cafe-producao-container">
      <div className="cafe-producao-card">
        <h1>Produção e Produtividade</h1>
        <p className="cafe-producao-subtitle">Preencha os campos para calcular os indicadores da sua lavoura.</p>

        {/* --- ESTRUTURA DE GRID ADICIONADA --- */}
        <div className="cafe-producao-form-results-grid">
            <div className="cafe-producao-form">
                <div className="cafe-producao-input-group">
                    <label>Data da Análise</label>
                    <input type="date" name="dataSelecionada" value={dados.producao?.dataSelecionada || ''} onChange={handleDateChange} />
                </div>
                <div className="cafe-producao-input-group">
                    <label>Produção Total da Área (sacas)</label>
                    <input type="text" name="producaoTotal" placeholder="Ex: 500,00" value={formatValueForDisplay(dados.producao?.producaoTotal)} onChange={handleNumericChange} />
                </div>
                <div className="cafe-producao-input-group">
                    <label>Área Plantada (hectares)</label>
                    <input type="text" name="areaPlantada" placeholder="Ex: 10,00" value={formatValueForDisplay(dados.producao?.areaPlantada)} onChange={handleNumericChange} />
                </div>
                <div className="cafe-producao-input-group">
                    <label>Nº de Colaboradores</label>
                    <input type="text" name="colaboradores" placeholder="Ex: 8,00" value={formatValueForDisplay(dados.producao?.colaboradores)} onChange={handleNumericChange} />
                </div>
                <button className="cafe-producao-button" onClick={handleCalcular}>Calcular</button>
            </div>

            {mostrarResultados && (
              <div className="cafe-producao-results">
                <h2>Resultados</h2>
                <ul>
                  <li>Sacas por Hectare: <strong>{dados.resultadosProducao?.sacasPorHectare} sc/ha</strong></li>
                  <li>Sacas por Colaborador: <strong>{dados.resultadosProducao?.sacasPorColaborador} sc/colab</strong></li>
                </ul>
                <button className="cafe-producao-button pdf" onClick={handleDownloadPDF}>Baixar Relatório (PDF)</button>
              </div>
            )}
        </div>

        <div className="cafe-producao-navigation">
          <button className="cafe-producao-button back" onClick={() => navigate('/calccafe/app')}>Voltar</button>
          <button className="cafe-producao-button clean" onClick={handleLimparDados}>Limpar Dados</button>
          <button className="cafe-producao-button navigate" onClick={() => navigate("/calccafe/app/capitalCafe")}>Avançar para Capital</button>
        </div>
      </div>
    </div>
  );
};

export default ProducaoCafe;

